import { useEffect, useState } from "react";
import { Pencil, Eye, Save } from "lucide-react";
import Builder from "./Builder";
import formAPI from "../../api/formApi";
import PreviewForm from "./PreviewForm";
import { showError, showSuccess } from "../../utils/toastUtils";
import {
  uid,
  makeField,
  validateFields,
  toPayload,
} from "../../utils/formBuilder";

const DEFAULT_FORM = {
  name: "New Form",
  strict: true,
  fields: [
    {
      id: uid(),
      type: "text",
      label: "Your Name",
      key: "name",
      required: true,
      min: 2,
      max: 80,
    },
  ],
};

/**
 * Props (sab optional):
 *  initialForm, formId
 */
export default function FormBuilder({ initialForm, formId }) {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [selectedId, setSelectedId] = useState(null);
  const [mode, setMode] = useState("build");
  const [toast, setToast] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (initialForm && Object.keys(initialForm).length) {
      setForm({
        ...initialForm,
        fields: (initialForm.fields || []).map((f) => ({
          ...f,
          id: f.id ?? f._id ?? uid(),
        })),
      });
    }
  }, [initialForm]);

  const addField = (type, atIndex) =>
    setForm((prev) => {
      const fields = [...prev.fields];
      const f = makeField(type, fields.length);
      if (atIndex == null || atIndex > fields.length) fields.push(f);
      else fields.splice(atIndex, 0, f);
      setSelectedId(f.id);
      return { ...prev, fields };
    });

  const updateField = (id, patch) =>
    setForm((prev) => ({
      ...prev,
      fields: prev.fields.map((f) => (f.id === id ? { ...f, ...patch } : f)),
    }));

  const removeField = (id) =>
    setForm((prev) => ({
      ...prev,
      fields: prev.fields.filter((f) => f.id !== id),
    }));

  const moveField = (fromId, toIndex) =>
    setForm((prev) => {
      const fields = [...prev.fields];
      const from = fields.findIndex((f) => f.id === fromId);
      if (from < 0) return prev;
      const [m] = fields.splice(from, 1);
      let t = toIndex;
      if (from < toIndex) t--;
      fields.splice(Math.max(0, Math.min(t, fields.length)), 0, m);
      return { ...prev, fields };
    });

  const save = async () => {
    if (!form.name.trim()) return showError("Give the form a name");
    if (!form.fields.length) return showError("Add at least one field");
    const err = validateFields(form.fields);
    if (err) return showError(err);
    try {
      let payload = {};

      if (form._id) {
        payload = {
          ...toPayload(form),
          formId: form._id,
        };
      } else {
        payload = toPayload(form);
      }

      const saved = await formAPI.addForm(payload);

      setSaving(true);

      if (saved.code === 200) {
        showSuccess(saved.message);

        if (!form._id) {
          setForm(DEFAULT_FORM);
        }
      } else {
        showError();
      }
    } catch (e) {
      showError(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <header className="d-flex align-items-center justify-content-between gap-2 py-2">
        <div className="d-flex align-items-center gap-2 fw-semibold">
          <span
            className="fb-icon bg-primary text-white"
            style={{ fontSize: ".7rem" }}
          >
            AW
          </span>
          Axesell Workbench
        </div>
        <div className="d-flex align-items-center gap-2">
          <div className="btn-group btn-group-sm" role="group">
            <button
              type="button"
              onClick={() => setMode("build")}
              className={`btn d-flex align-items-center gap-1 ${mode === "build" ? "btn-primary" : "btn-outline-secondary"}`}
            >
              <Pencil size={14} /> Build
            </button>
            <button
              type="button"
              onClick={() => setMode("preview")}
              className={`btn d-flex align-items-center gap-1 ${mode === "preview" ? "btn-primary" : "btn-outline-secondary"}`}
            >
              <Eye size={14} /> Preview
            </button>
          </div>
        </div>
      </header>

      {mode === "build" ? (
        <>
          <div className="row mb-3">
            <div className="col-lg-3 col-sm-6">
              <label className="form-label">
                Title
                <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                placeholder="Title (e.g., Bulk Order)"
                className="form-control"
                name="title"
                value={form.name}
                onChange={(e) =>
                  setForm((p) => ({ ...p, name: e.target.value }))
                }
              />
            </div>
          </div>

          <Builder
            form={form}
            selectedId={selectedId}
            setSelectedId={setSelectedId}
            addField={addField}
            removeField={removeField}
            moveField={moveField}
            updateField={updateField}
            setStrict={(v) => setForm((p) => ({ ...p, strict: v }))}
          />

          <div className="row mt-3">
            <div className="col-12 d-flex justify-content-end">
              {!saving && (
                <button
                  onClick={save}
                  disabled={saving}
                  id="submitBtn"
                  className="btn btn-primary shadow-none"
                >
                  Submit Form Requirements
                </button>
              )}
            </div>
          </div>
        </>
      ) : (
        <PreviewForm form={form} />
      )}
    </>
  );
}
